/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1792108800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Oct 16 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "braze",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Braze",
          "depth": 0,
          "id": "connect-to-braze"
        }, {
          "value": "API key",
          "depth": 1,
          "id": "api-key"
        }, {
          "value": "Region",
          "depth": 1,
          "id": "region"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Event",
          "depth": 1,
          "id": "event"
        }, {
          "value": "Object",
          "depth": 1,
          "id": "object"
        }, {
          "value": "Subscription Status",
          "depth": 1,
          "id": "subscription-status"
        }, {
          "value": "Merge anonymous and known users",
          "depth": 1,
          "id": "merge-anonymous-and-known-users"
        }, {
          "value": "Catalog",
          "depth": 1,
          "id": "catalog"
        }, {
          "value": "Trigger campaigns",
          "depth": 1,
          "id": "trigger-campaigns"
        }, {
          "value": "Trigger Canvases",
          "depth": 1,
          "id": "trigger-canvases"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Behavior and limitations",
          "depth": 1,
          "id": "behavior-and-limitations"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Required permissions",
          "depth": 1,
          "id": "required-permissions-1"
        }, {
          "value": "Slow initial sync",
          "depth": 1,
          "id": "slow-initial-sync"
        }, {
          "value": "Audience count drops to 0 when adding Braze as a destination",
          "depth": 1,
          "id": "audience-count-drops-to-0-when-adding-braze-as-a-destination"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      hr: "hr",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      code: "code",
      h3: "h3",
      img: "img",
      h4: "h4",
      ul: "ul",
      li: "li",
      pre: "pre",
      span: "span",
      ol: "ol",
      em: "em",
      h5: "h5"
    }, _provideComponents(), props.components), {EventSyncWarning, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Braze is a customer engagement platform that supports real-time and batch messaging across email, SMS, mobile, and web. Use Hightouch to sync user data, events, subscription status, and catalog information so Braze can run data-driven messaging and lifecycle campaigns."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#event",
                children: _jsx(_components.strong, {
                  children: "Event"
                })
              })
            }), _jsx(_components.td, {
              children: "Send track events and track purchase type events to Braze"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://www.braze.com/docs/api/objects_filters/event_object/",
                children: "Track events ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#object",
                children: _jsx(_components.strong, {
                  children: "Object"
                })
              })
            }), _jsxs(_components.td, {
              children: ["Sync records to Braze objects like ", _jsx(_components.code, {
                children: "User"
              }), " or ", _jsx(_components.code, {
                children: "Organization"
              }), "."]
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://www.braze.com/docs/api/endpoints/user_data/",
                children: "Update user data ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#subscription-status",
                children: _jsx(_components.strong, {
                  children: "Subscription Status"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync SMS or email subscription groups."
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://www.braze.com/docs/api/endpoints/subscription_groups/",
                children: "Manage subscription groups ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#merge-anonymous-and-known-users",
                children: _jsx(_components.strong, {
                  children: "User Identify"
                })
              })
            }), _jsx(_components.td, {
              children: "Merge alias-only users with an identified profile and remove the alias-only profile"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://www.braze.com/docs/api/endpoints/user_data",
                children: "Identify users ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#catalog",
                children: _jsx(_components.strong, {
                  children: "Catalog"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync catalog items such as products or offers."
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://www.braze.com/docs/api/endpoints/catalogs",
                children: "Manage catalog items ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#trigger-campaigns",
                children: _jsx(_components.strong, {
                  children: "Campaign Trigger"
                })
              })
            }), _jsx(_components.td, {
              children: "Trigger campaigns with context from your records."
            }), _jsx(_components.td, {
              children: "Insert, Update, Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://www.braze.com/docs/api/endpoints/messaging/send_messages/post_send_triggered_campaigns/",
                children: "Send triggered campaigns ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#trigger-canvases",
                children: _jsx(_components.strong, {
                  children: "Canvas Trigger"
                })
              })
            }), _jsx(_components.td, {
              children: "Trigger Canvases with context from your records."
            }), _jsx(_components.td, {
              children: "Insert, Update, Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://www.braze.com/docs/api/endpoints/messaging/send_messages/post_send_triggered_canvases/",
                children: "Send triggered Canvases ↗"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "connect-to-braze",
        children: "Connect to Braze"
      }), "\n", _jsxs(_components.p, {
        children: ["Connecting Braze to Hightouch requires an ", _jsx(_components.strong, {
          children: "API key"
        }), " and ", _jsx(_components.strong, {
          children: "region"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "api-key",
        children: "API key"
      }), "\n", _jsxs(_components.p, {
        children: ["Generate or locate a REST API key in your Braze dashboard under ", _jsx(_components.strong, {
          children: "Settings > APIs and Identifiers > API Keys"
        }), ". Create a dedicated REST API key for Hightouch rather than reusing a Partner Integration key — Partner Integration keys don't include the permissions that Hightouch requires."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/destinations/destination-braze-api-key.png",
          alt: "Finding an API key in the Braze dashboard"
        })
      }), "\n", _jsx(_components.h4, {
        id: "required-permissions",
        children: "Required permissions"
      }), "\n", _jsx(_components.p, {
        children: "The API key must include permissions for each sync type you plan to use."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Required permissions"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Event"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "users.track"
              }), ", ", _jsx(_components.code, {
                children: "custom_attributes.get"
              }), ", ", _jsx(_components.code, {
                children: "users.export.ids"
              }), " (only required if using the ", _jsx(_components.strong, {
                children: "Retry"
              }), " ", _jsx(_components.a, {
                href: "#event",
                children: "user existence behavior"
              }), ")"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Object"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "users.track"
              }), ", ", _jsx(_components.code, {
                children: "users.export.ids"
              }), ", ", _jsx(_components.code, {
                children: "users.delete"
              }), " (optional if you want to rely on Hightouch for user deletions), ", _jsx(_components.code, {
                children: "users.alias.update"
              }), " (if you are updating existing users), ", _jsx(_components.code, {
                children: "custom_attributes.get"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Subscription Status"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "subscription.status.set"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "User Identify"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "users.identify"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Catalog"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "catalogs.add_items"
              }), ", ", _jsx(_components.code, {
                children: "catalogs.update_items"
              }), ", ", _jsx(_components.code, {
                children: "catalogs.get_item"
              }), ", ", _jsx(_components.code, {
                children: "catalogs.create"
              }), ", ", _jsx(_components.code, {
                children: "catalogs.get"
              }), ", ", _jsx(_components.code, {
                children: "catalogs.delete_items"
              }), ", ", _jsx(_components.code, {
                children: "catalogs.replace_items"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Campaign Trigger"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "campaigns.trigger.send"
              }), ", ", _jsx(_components.code, {
                children: "campaigns.list"
              }), ", ", _jsx(_components.code, {
                children: "campaigns.details"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Canvas Trigger"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "canvas.trigger.send"
              }), ", ", _jsx(_components.code, {
                children: "canvas.list"
              }), ", ", _jsx(_components.code, {
                children: "canvas.details"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Segments"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "segments.list"
              }), ", ", _jsx(_components.code, {
                children: "segments.details"
              }), " (only required if you use Braze with Hightouch ", _jsx(_components.a, {
                href: "/agents/overview",
                children: "Agents"
              }), ")"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "region",
        children: "Region"
      }), "\n", _jsx(_components.p, {
        children: "To find your Braze region, check the domain in your Braze dashboard URL and match it to the table below."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/destinations/destination-braze-region.png",
          alt: "Find your Braze region from your dashboard URL"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "URL"
            }), _jsx(_components.th, {
              children: "Region"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "https://dashboard-01.braze.com"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "iad-01.braze.com"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "https://dashboard-02.braze.com"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "iad-02.braze.com"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "https://dashboard-03.braze.com"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "iad-03.braze.com"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "https://dashboard-04.braze.com"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "iad-04.braze.com"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "https://dashboard-05.braze.com"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "iad-05.braze.com"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "https://dashboard-06.braze.com"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "iad-06.braze.com"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "https://dashboard-07.braze.com"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "iad-07.braze.com"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "https://dashboard-08.braze.com"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "iad-08.braze.com"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "https://dashboard-01.braze.eu"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "fra-01.braze.eu"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "https://dashboard-02.braze.eu"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "fra-02.braze.eu"
              })
            })]
          })]
        })]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Refer to the ", _jsx(_components.a, {
            href: "https://www.braze.com/docs/api/basics/?redirected=true#api-definitions",
            children: "Braze endpoint\ndocumentation"
          }), "\nto view additional regions. To derive the region, remove the prefix\n\"", _jsx(_components.code, {
            children: "https://rest"
          }), "\" from the respective value in the ", _jsx(_components.strong, {
            children: "REST ENDPOINT"
          }), " column."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.p, {
        children: "The configuration options below vary by sync type."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Destination fields appear greyed out until you select a sync type and\nconfigure record matching. This is expected behavior during initial setup, not\nan error."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "event",
        children: "Event"
      }), "\n", _jsx(_components.p, {
        children: "Send events to Braze and assign them to a user. Choose between two event types:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Track Event:"
          }), " Sends events of a particular name when a record is added"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Track Purchase:"
          }), " Track when a customer makes a purchase"]
        }), "\n"]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsxs(_components.p, {
        children: ["When syncing events, you can configure what happens when the associated user doesn't exist in Braze yet. This is controlled by the ", _jsx(_components.strong, {
          children: "user existence behavior"
        }), " setting:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Create user"
          }), ": Braze automatically creates a new user profile when the event is received."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Skip event"
          }), ": Braze won't create a new profile or track the event."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Retry until user exists"
          }), ": Hightouch polls Braze to check whether the user exists before sending the event. If the user hasn't been created after 2 minutes, the event is rejected as retriable and retried later."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/destinations/destination-braze-create-users-events.png",
          alt: "Event configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch can match events to users on different Braze fields. If user existence behavior is set to ", _jsx(_components.strong, {
          children: "Create user"
        }), ", you can only match on ", _jsx(_components.strong, {
          children: "Braze External ID"
        }), " or ", _jsx(_components.strong, {
          children: "User Alias Object"
        }), ". If set to ", _jsx(_components.strong, {
          children: "Skip event"
        }), " or ", _jsx(_components.strong, {
          children: "Retry until user exists"
        }), ", you can also match on ", _jsx(_components.strong, {
          children: "Braze ID"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["For ", _jsx(_components.code, {
          children: "track event"
        }), " type events you can also send the event name and event timestamp. ", _jsx(_components.code, {
          children: "Track purchases"
        }), " don't require an event name."]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Hightouch accepts a standard date time format for the timestamp and\nautomatically converts it to the format Braze requires."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-braze-track-config.png",
          alt: "Record matching for events in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Map model columns to the event properties of a Braze event."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-braze-event-properties.png",
          alt: "Field mapping for events in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "event-property-configuration",
        children: "Event property configuration"
      }), "\n", _jsx(_components.p, {
        children: "Send your custom properties as a single object column or as multiple columns from your model."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-braze-event-property-object.png",
          alt: "Event property configuration in the Hightouch UI"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["When using a single column, make sure that the model column's ", _jsx(_components.a, {
            href: "/models/data-types-casting#data-types",
            children: "data\ntype"
          }), " is ", _jsx(_components.strong, {
            children: "Object / Array"
          }), "."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "object",
        children: "Object"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync rows from your model to Braze users. Use ", _jsx(_components.strong, {
          children: "Upsert"
        }), " when Hightouch should create missing users and keep all attributes up to date. Use ", _jsx(_components.strong, {
          children: "Update"
        }), " when only users that already exist in Braze should change."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch automatically normalizes some user data fields to meet Braze's specifications."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Field name"
            }), _jsx(_components.th, {
              children: "Hightouch normalization"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Date of birth"
            }), _jsx(_components.td, {
              children: "Convert from timestamps to year-month-date"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Email"
            }), _jsx(_components.td, {
              children: "Convert to all lowercase letters"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Phone numbers"
            }), _jsxs(_components.td, {
              children: ["Remove leading plus signs (", _jsx(_components.code, {
                children: "+"
              }), ") and white spaces"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "In Upsert mode, you can match records from your source to your Braze workspace using the Braze External User ID. In Update mode, you can also match records based on Braze ID or a user alias object."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["If you match records with a column ", _jsx(_components.strong, {
            children: "other"
          }), " than the primary key of your\nmodel, changes in the model column used for record matching will not be\nreflected in Braze. This is to ensure duplicate records are not created in\nBraze."]
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["When syncing a large amount of rows, make sure to match the records using\nBraze External User ID instead of Braze ID. Using the Braze ID requires more\nrequests which often results in slower sync speeds. See the ", _jsx(_components.a, {
            href: "#slow-initial-sync",
            children: "slow initial sync\nsection"
          }), " for more details."]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/destinations/destination-braze-record-matching.png",
          alt: "Selecting a model column to use as the Braze external ID in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Braze's default and custom fields. If you send data for a custom field that doesn't exist, Hightouch adds the field and automatically detects its type."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-braze-field-mapping.png",
          alt: "Mapping model columns to fields in Braze"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can manage your custom fields in ", _jsx(_components.strong, {
          children: "Manage App Group > Custom Attributes"
        }), " in Braze."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can also map your model columns to ", _jsx(_components.strong, {
          children: "user aliases"
        }), " in Braze.\nThis configuration only appears if you select ", _jsx(_components.strong, {
          children: "Braze External ID"
        }), " for ", _jsx(_components.a, {
          href: "/destinations/braze#record-matching",
          children: "record matching"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/destinations/destination-braze-field-mapping-user-alias.png",
          alt: "Field mapping to Braze user aliases"
        })
      }), "\n", _jsx(_components.h4, {
        id: "nested-objects-and-object-arrays",
        children: "Nested objects and object arrays"
      }), "\n", _jsxs(_components.p, {
        children: ["Braze supports user attributes containing JSON objects or ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/user_guide/data_and_analytics/custom_data/custom_attributes/array_of_objects/",
          children: "object arrays"
        }), ". These objects and object arrays are commonly used to support \"entities\" related to each user—for example, devices, medications, pets, etc.—especially when marketing communication needs to include metadata about these related entities."]
      }), "\n", _jsx(_components.p, {
        children: "To sync to these attributes, build the full JSON object into a single column within your Hightouch data model."
      }), "\n", _jsxs(_components.p, {
        children: ["Modeling nested attributes into JSON can be done directly in your warehouse with something like ", _jsx(_components.a, {
          href: "https://www.getdbt.com/",
          children: "dbt"
        }), ", or you can use SQL functions appropriate for your data warehouse and build these objects within the ", _jsx(_components.a, {
          href: "/models/sql-editor",
          children: "SQL Editor"
        }), " in Hightouch."]
      }), "\n", _jsx(_components.h4, {
        id: "nested-object-array-example",
        children: "Nested object array example"
      }), "\n", _jsxs(_components.p, {
        children: ["Let's say you want an array of a customer's pets to be an attribute named ", _jsx(_components.code, {
          children: "pets"
        }), " in Braze. You would need a data model with a column named ", _jsx(_components.code, {
          children: "pets"
        }), " and would then build the full JSON pets object array to populate that column. This array might look something like this:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "0"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"dog\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"breed\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Mutt\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Rae\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"cat\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"breed\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Tabby\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Garfield\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "This full object array would need to be the value of a column in your Hightouch data model:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-braze-pets-array.png",
          alt: "Value of an example \"Pets\" field shown in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Then you would map this column to the ", _jsx(_components.code, {
          children: "pets"
        }), " custom attribute in Braze in your Hightouch sync configuration:"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-braze-pets-mapping.png",
          alt: "Mapping the \"Pets\" field in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "After scheduling your sync, you'd have relevant pet data on each user in Braze."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-braze-pets.png",
          alt: "Pet data appearing in the Braze UI"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "subscription-status",
        children: "Subscription Status"
      }), "\n", _jsx(_components.p, {
        children: "Sync users to Email and SMS subscription groups."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/destinations/destination-braze-subscription-types.png",
          alt: "Subscription status configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Braze has a designated set of fields to manage subscription group opt-ins independent of user attributes."
      }), "\n", _jsx(_components.p, {
        children: "Sync to existing subscription groups within Braze. You can enter a static subscription group ID or select a column from your model containing the subscription group ID."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/destinations/destination-braze-subscription-id.png",
          alt: "Subscription group configuration in the Hightouch UI"
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Read ", _jsx(_components.a, {
            href: "https://www.braze.com/docs/user_guide/message_building_by_channel/email/managing_user_subscriptions/",
            children: "Braze's\ndocs"
          }), "\nfor information on creating a subscription group."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-2",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match records from your source to your Braze subscription groups by Braze External User ID, Phone Number, or Email."
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["When a user is removed from a Subscription status sync, Hightouch makes an API\nrequest to set the user's\n", _jsx(_components.a, {
            href: "https://www.braze.com/docs/api/endpoints/subscription_groups/post_update_user_subscription_group_status_v2/#request-body",
            children: _jsx(_components.code, {
              children: "subscription_state"
            })
          }), "\nto ", _jsx(_components.code, {
            children: "unsubscribed"
          }), "."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "merge-anonymous-and-known-users",
        children: "Merge anonymous and known users"
      }), "\n", _jsx(_components.p, {
        children: "Merge alias-only \"anonymous\" users with \"known\" Braze users that have an external ID."
      }), "\n", _jsx(_components.p, {
        children: "It's common to have “soft-signup” workflows where all you collect from a user is an email or phone without forcing the user to create an account. This workflow results in users where all you may have is PII. When they create an account, you need to use this endpoint to “identify” these users with a new login ID."
      }), "\n", _jsx(_components.p, {
        children: "For example, your workflow might look like this:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "A user visits your website or app. The user doesn't have a Braze ID to start with—they only enter an email address, phone number, or some other PII. You create a user in you customer database, and an alias-only user profile in Braze via the Braze SDK. Hightouch isn't involved at this point."
        }), "\n", _jsxs(_components.li, {
          children: ["You can use Hightouch to send user attributes or user-related events to Braze via Hightouch by ", _jsx(_components.a, {
            href: "#record-matching",
            children: "matching on a user alias object"
          }), ", since there isn't an external ID yet. You must use Update mode."]
        }), "\n", _jsxs(_components.li, {
          children: ["Once you have a unique user ID in your customer database, you can use Hightouch to call Braze's ", _jsx(_components.a, {
            href: "https://www.braze.com/docs/api/endpoints/user_data/post_user_identify/",
            children: "Identify endpoint"
          }), " to \"identify\" the alias-only user using your unique user ID as the external ID in Braze. This merges the alias-only user in Braze with a known Braze user. If an external ID doesn't exist when using the Identify endpoint, Braze creates the external ID and the alias-only user is considered “identified.”"]
        }), "\n", _jsx(_components.li, {
          children: "All subsequent updates to the user should be done in Hightouch using the external user ID, not the user alias."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-3",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match and merge records using Braze user external IDs."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/destinations/destination-braze-identify-record-matching.png",
          alt: "Selecting a model column to use as the Braze external ID in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Map your Braze aliases to known users using the alias label and value."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/destinations/destination-braze-identify-field-mapping.png",
          alt: "Field mapping for users in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you're adding a JSON object directly to your model, each ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/api/objects_filters/user_alias_object",
          children: _jsx(_components.code, {
            children: "user_alias"
          })
        }), " object should consist of ", _jsx(_components.code, {
          children: "alias_label"
        }), " and ", _jsx(_components.code, {
          children: "alias_name"
        }), " as top-level attributes, rather than nested in another object."]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "# CORRECT FORMAT\n\"user_aliases\": [\n    {\n      \"alias_label\": \"label\",\n      \"alias_name\": \"name_1\"\n    },\n    ...\n  ]\n\n# INCORRECT FORMAT\n\"user_aliases\": [\n    {\n      \"user_alias\" : {\n        \"alias_name\" : \"label\",\n        \"alias_label\" : \"name_1\"\n      }\n    },\n    ,\n    ...\n  ]\n\n"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "catalog",
        children: "Catalog"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync rows from your models to your existing catalog through the ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/api/endpoints/catalogs",
          children: "Braze Catalogs API"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Braze's Catalog endpoint is in early access. Contact your Braze account\nmanager if you are interested in participating."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can choose to create a new ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/user_guide/personalization_and_dynamic_content/catalogs/catalog/",
          children: "Braze catalog"
        }), " the first time the\nsync runs or use an existing catalog. If creating a new catalog, you can name the catalog from Hightouch. If you leave this field blank, the name defaults to the\nmodel name."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/destinations/destination-braze-auto-create-catalog.png",
          alt: "Hightouch UI showing option to create new catalogs"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["With the ", _jsx(_components.strong, {
            children: "Create a new catalog"
          }), " option enabled, Hightouch only creates a\ncatalog the first time the sync runs. Hightouch doesn't modify the catalog\nname after the first run. If you want to change the name or fields, you have\nto create a new catalog using a new sync."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Upsert"
        }), " to insert new catalog items into Braze. Use ", _jsx(_components.strong, {
          children: "Update"
        }), " to update fields on catalog items that already exist in Braze."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-4",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "In Upsert and Update modes, you can match records from your source to your Braze workspace using the catalog item ID."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-3",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map data from your source to any existing field in your catalog."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "If automatically creating a catalog, you can't add new fields or change data\ntypes after the initial sync."
        })
      }), "\n", _jsx(_components.h4, {
        id: "test-a-catalog-sync",
        children: "Test a catalog sync"
      }), "\n", _jsx(_components.p, {
        children: "If you are using an existing catalog, you can test your sync by syncing a single row. You can verify that a row was successfully synced by going to your Catalog and finding the synced row by its catalog item ID. If you are automatically creating a catalog, it's not possible to test by row."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "trigger-campaigns",
        children: "Trigger campaigns"
      }), "\n", _jsxs(_components.p, {
        children: ["Use records from your models to trigger campaigns through the ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/api/endpoints/messaging/send_messages/post_send_triggered_campaigns/",
          children: "Braze Campaign API"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Once configured, your Hightouch sync triggers your campaign once rows appear in your model. Hightouch triggers your campaign for every row in your model the first time you run the sync, whenever you do a full resync, and for every new row added to your query results in subsequent syncs."
      }), "\n", _jsx(_components.h4, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsxs(_components.p, {
        children: ["The sync mode controls which rows re-trigger the campaign on subsequent runs (", _jsx(_components.a, {
          href: "/syncs/cdc#cdc-by-sync-mode",
          children: "CDC behavior"
        }), "). It doesn't change the underlying Braze API call. To control whether messages are only sent to users that already exist in Braze, use ", _jsx(_components.a, {
          href: "#send-to-existing-only",
          children: "Send to existing only"
        }), "."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), " (default): triggers the campaign for every row the first time you run the sync and on every full resync, and for every new row added to your query results in subsequent syncs."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update"
          }), ": triggers the campaign only for rows that change in your model."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), ": triggers the campaign for new rows and for rows that change in your model."]
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["In Update and Upsert modes, a row that changes re-triggers the campaign, so\nthe same user can receive duplicate messages. Set ", _jsx(_components.a, {
            href: "https://www.braze.com/docs/user_guide/engagement_tools/campaigns/building_campaigns/delivery_types/reeligibility/#canvas",
            children: "delivery\ncontrols"
          }), "\nin Braze to prevent duplicates."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "prerequisites",
        children: "Prerequisites"
      }), "\n", _jsxs(_components.p, {
        children: ["Before you can set up a Hightouch sync to trigger a campaign, you need set up an ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/user_guide/engagement_tools/campaigns/building_campaigns/delivery_types/api_triggered_delivery/",
          children: "API-triggered"
        }), " campaign in Braze. See ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/user_guide/message_building_by_channel/email/creating_an_email_campaign/",
          children: "how to create an email campaign in Braze"
        }), " for more information."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Hightouch can only trigger campaigns that are\n", _jsx(_components.a, {
            href: "https://www.braze.com/docs/user_guide/engagement_tools/campaigns/building_campaigns/delivery_types/api_triggered_delivery/",
            children: "API-triggered"
          }), ".\nThese are different from ", _jsx(_components.a, {
            href: "https://www.braze.com/docs/api/api_campaigns/",
            children: "API\ncampaigns"
          }), "."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-5",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Trigger a campaign for your Braze users by matching on one of these identifiers:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "External user ID"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Braze ID"
          }), ": Matches the ID Braze assigns to every profile, including anonymous profiles that don't have an external user ID. ", _jsx(_components.a, {
            href: "#send-to-existing-only",
            children: "Send to existing only"
          }), " isn't available, because a Braze ID can only match a profile that already exists in Braze."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Email address"
          }), ": When several Braze profiles share an email address, Braze uses a prioritization to choose one. Set the order in the prioritization field. The default is ", _jsx(_components.strong, {
            children: "Identified"
          }), ", then ", _jsx(_components.strong, {
            children: "Most recently updated"
          }), ". Braze only sends when the prioritization resolves to a single profile, and you can't select both ", _jsx(_components.strong, {
            children: "Identified"
          }), " and ", _jsx(_components.strong, {
            children: "Unidentified"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User alias object"
          }), ": Map an object column with ", _jsx(_components.code, {
            children: "alias_name"
          }), " and ", _jsx(_components.code, {
            children: "alias_label"
          }), " fields. ", _jsx(_components.a, {
            href: "#send-to-existing-only",
            children: "Send to existing only"
          }), " isn't available, because Braze doesn't support it for user aliases."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch rejects rows with an empty email address, a Braze ID that isn't a 24-character hexadecimal string, or an invalid user alias object."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["This destination doesn't check for duplicates. Hightouch recommends that you\nset ", _jsx(_components.a, {
            href: "https://www.braze.com/docs/user_guide/engagement_tools/campaigns/building_campaigns/delivery_types/reeligibility/#canvas",
            children: "delivery\ncontrols"
          }), "\nin Braze to prevent duplicated campaign messages in case your source has\nduplicates."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-4",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Customize your messages via ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/api/objects_filters/trigger_properties_object",
          children: "API trigger properties"
        }), ". Hightouch automatically lists trigger properties you can map values with. See ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/user_guide/engagement_tools/campaigns/building_campaigns/delivery_types/api_triggered_delivery/#setting-up-an-api-triggered-campaign",
          children: "using the templated content included with an API request"
        }), " for more information."]
      }), "\n", _jsx(_components.h4, {
        id: "send-to-existing-only",
        children: "Send to existing only"
      }), "\n", _jsxs(_components.p, {
        children: ["Keep ", _jsx(_components.strong, {
          children: "Send to existing only"
        }), " enabled when the campaign should target only users that already exist in Braze. To create or update users when you trigger the campaign, uncheck the setting and map the user properties."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/destinations/destination-braze-send-to-existing-only.png",
          alt: "Send to existing only"
        })
      }), "\n", _jsxs(Info, {
        children: ["Note: Mapping user properties when using this setting is ", _jsx(_components.em, {
          children: "required"
        })]
      }), "\n", _jsx(_components.h4, {
        id: "test-campaign-triggers-safely",
        children: "Test campaign triggers safely"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch recommends that you test with your email before running an actual sync for your users."
      }), "\n", _jsx(_components.p, {
        children: "You can test by following these steps:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Create a Google Sheet with a valid ", _jsx(_components.code, {
            children: "external_id"
          }), " and your email. You will use this to sync yourself as user in Braze. See ", _jsx(_components.a, {
            href: "https://www.braze.com/docs/developer_guide/platform_integration_guides/web/analytics/setting_user_ids/#suggested-user-id-naming-convention",
            children: "Braze's user ID convention"
          }), " if you are unfamiliar with ", _jsx(_components.code, {
            children: "external_id"
          }), ".\n", _jsx(_components.img, {
            src: "/destinations/destination-braze-campaign-trigger-test-sheet.png",
            alt: "Screenshot of Google Sheet with test information"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Use the Google Sheet as a source to ", _jsx(_components.a, {
            href: "/destinations/braze#object",
            children: "add yourself to Braze as a user"
          }), ".\n", _jsx(_components.img, {
            src: "/destinations/destination-braze-campaign-trigger-sync-user.png",
            alt: "Syncing information from Google Sheet to Braze using Hightouch"
          }), "\n", _jsx(Tip, {
            children: _jsx(_components.p, {
              children: "You can use the same sync to remove yourself from Braze once your testing\nis complete. Set the sync configuration's delete mode to \"Delete\ndestination record associated with source record,\" remove yourself from the\nsource sheet and rerun the sync."
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Build your campaign in Braze. See Braze's guide on ", _jsx(_components.a, {
            href: "https://www.braze.com/docs/user_guide/engagement_tools/campaigns/building_campaigns",
            children: "how to build a campaign"
          }), " for more information. When setting up your campaign schedule, make sure to select ", _jsx(_components.a, {
            href: "https://www.braze.com/docs/user_guide/engagement_tools/campaigns/building_campaigns/delivery_types/api_triggered_delivery/",
            children: "API-triggered Delivery"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Set up your campaign trigger sync and run it on Hightouch."
        }), "\n", _jsxs(_components.li, {
          children: ["Verify that you are able to receive your campaign message. If you need to make corrections and thus want to re-run the sync, click ", _jsx(_components.strong, {
            children: "Resync full query"
          }), " next to the ", _jsx(_components.strong, {
            children: "Run"
          }), " button. Also note that your ", _jsx(_components.a, {
            href: "https://www.braze.com/docs/user_guide/engagement_tools/campaigns/building_campaigns/delivery_types/reeligibility/#canvas",
            children: "re-eligibility settings"
          }), " in Braze campaign must be loosely set for you to re-trigger the sync and actually receive the email."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "trigger-canvases",
        children: "Trigger Canvases"
      }), "\n", _jsxs(_components.p, {
        children: ["Use records from your models to trigger Canvases through the ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/api/endpoints/messaging/send_messages/post_send_triggered_canvases/",
          children: "Braze Canvas API"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Once configured, your Hightouch sync triggers your Canvases once rows appear in your model. Hightouch triggers your Canvases for every row in your model the first time you run the sync, whenever you do a full resync, and for every new row added to your query results in subsequent syncs."
      }), "\n", _jsx(_components.h4, {
        id: "sync-modes-1",
        children: "Sync modes"
      }), "\n", _jsxs(_components.p, {
        children: ["The sync mode controls which rows re-trigger the Canvas on subsequent runs (", _jsx(_components.a, {
          href: "/syncs/cdc#cdc-by-sync-mode",
          children: "CDC behavior"
        }), "). It doesn't change the underlying Braze API call. To control whether messages are only sent to users that already exist in Braze, use ", _jsx(_components.a, {
          href: "#send-to-existing-only",
          children: "Send to existing only"
        }), "."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), " (default): triggers the Canvas for every row the first time you run the sync and on every full resync, and for every new row added to your query results in subsequent syncs."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update"
          }), ": triggers the Canvas only for rows that change in your model."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), ": triggers the Canvas for new rows and for rows that change in your model."]
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["In Update and Upsert modes, a row that changes re-triggers the Canvas, so the\nsame user can receive duplicate messages. Set ", _jsx(_components.a, {
            href: "https://www.braze.com/docs/user_guide/engagement_tools/campaigns/building_campaigns/delivery_types/reeligibility/#canvas",
            children: "delivery\ncontrols"
          }), "\nin Braze to prevent duplicates."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "prerequisites-1",
        children: "Prerequisites"
      }), "\n", _jsxs(_components.p, {
        children: ["Before you can set up a Hightouch sync to trigger a Canvas, you need set up an ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/user_guide/engagement_tools/canvas/create_a_canvas/create_a_canvas/#step-2b-set-your-canvas-entry-schedule",
          children: "API-triggered"
        }), " Canvas in Braze. See ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/user_guide/engagement_tools/canvas/ideas_and_strategies",
          children: "Braze Ideas & Strategies"
        }), " for more information and best practices."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Hightouch can only trigger Canvases that are\n", _jsx(_components.a, {
            href: "https://www.braze.com/docs/user_guide/engagement_tools/canvas/create_a_canvas/create_a_canvas/#step-2b-set-your-canvas-entry-schedule",
            children: "API-triggered"
          }), ".\nOnly non-archived, active canvases are available in sync configuration."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-6",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Trigger a Canvas for your Braze users by matching on one of these identifiers:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "External user ID"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Braze ID"
          }), ": Matches the ID Braze assigns to every profile, including anonymous profiles that don't have an external user ID. ", _jsx(_components.a, {
            href: "#send-to-existing-only",
            children: "Send to existing only"
          }), " isn't available, because a Braze ID can only match a profile that already exists in Braze."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Email address"
          }), ": When several Braze profiles share an email address, Braze uses a prioritization to choose one. Set the order in the prioritization field. The default is ", _jsx(_components.strong, {
            children: "Identified"
          }), ", then ", _jsx(_components.strong, {
            children: "Most recently updated"
          }), ". Braze only sends when the prioritization resolves to a single profile, and you can't select both ", _jsx(_components.strong, {
            children: "Identified"
          }), " and ", _jsx(_components.strong, {
            children: "Unidentified"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User alias object"
          }), ": Map an object column with ", _jsx(_components.code, {
            children: "alias_name"
          }), " and ", _jsx(_components.code, {
            children: "alias_label"
          }), " fields. ", _jsx(_components.a, {
            href: "#send-to-existing-only",
            children: "Send to existing only"
          }), " isn't available, because Braze doesn't support it for user aliases."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch rejects rows with an empty email address, a Braze ID that isn't a 24-character hexadecimal string, or an invalid user alias object."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-5",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Customize your messages via ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/api/objects_filters/canvas_entry_properties_object",
          children: "Canvas entry properties"
        }), ". Hightouch automatically lists Canvas entry properties you can map values to."]
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/user_guide/engagement_tools/canvas/create_a_canvas/canvas_entry_properties_event_properties",
          children: "Braze's docs"
        }), " on using the templated content included with an API request for more information."]
      }), "\n", _jsx(_components.h4, {
        id: "send-to-existing-only-1",
        children: "Send to existing only"
      }), "\n", _jsxs(_components.p, {
        children: ["Keep ", _jsx(_components.strong, {
          children: "Send to existing only"
        }), " enabled when the Canvas should target only users that already exist in Braze. To create or update users when you trigger the Canvas, uncheck the setting and map the user properties."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/destinations/destination-braze-send-to-existing-only.png",
          alt: "Send to existing only"
        })
      }), "\n", _jsxs(Info, {
        children: ["Note: Mapping user properties when using this setting is ", _jsx(_components.em, {
          children: "required"
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to Braze, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#braze",
            children: "Error codes:\nBraze"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "behavior-and-limitations",
        children: "Behavior and limitations"
      }), "\n", _jsx(_components.h4, {
        id: "null-values-clear-profile-attributes",
        children: "NULL values clear profile attributes"
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing user attributes to Braze, any mapped field containing a NULL value ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/api/objects_filters/user_attributes_object#braze-user-profile-fields",
          children: "clears the corresponding attribute"
        }), " on the Braze user profile. If you don't want to overwrite existing attribute values with empty data, exclude NULL rows from your model using a SQL ", _jsx(_components.code, {
          children: "WHERE"
        }), " clause, or select the ", _jsx(_components.strong, {
          children: "Don't sync NULL values"
        }), " option in your sync's ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping configuration"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "syncs-consume-braze-data-points",
        children: "Syncs consume Braze data points"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch ", _jsx(_components.a, {
          href: "/getting-started/concepts#change-data-capture-cdc",
          children: "only syncs data that needs updating"
        }), " to minimize the number of API requests made to your destinations."]
      }), "\n", _jsx(_components.p, {
        children: "Braze offers different pricing models, and how Hightouch syncs impact your usage depends on which model you're using."
      }), "\n", _jsx(_components.p, {
        children: "To optimize for speed instead of data point updates, you can disable data point optimization in your Hightouch sync configuration."
      }), "\n", _jsx(_components.h5, {
        id: "data-points-based-pricing",
        children: "Data points based pricing"
      }), "\n", _jsx(_components.p, {
        children: "In this model, pricing is based on data points, which count the number of updates made by you (and Hightouch). Each update contributes to your total data point usage, so efficient syncing can help reduce consumption."
      }), "\n", _jsx(_components.h5, {
        id: "monthly-active-users-mau-based-pricing-cy2425-sku",
        children: "Monthly Active Users (MAU) based pricing (CY24–25 SKU)"
      }), "\n", _jsx(_components.p, {
        children: "This pricing model emphasizes data flexibility for customers. There is no concept of \"data points\" in this SKU. However, overall data consumption is still constrained by product limits such as API rate limits and other usage thresholds, as detailed in Braze's documentation."
      }), "\n", _jsx(_components.h5, {
        id: "how-data-points-are-consumed",
        children: "How data points are consumed"
      }), "\n", _jsx(_components.p, {
        children: "These are some key points to understand how Hightouch syncs affect your Braze data point consumption."
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing arrays without nested objects, you consume one Braze ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/user_guide/data_and_analytics/data_points/",
          children: "data point"
        }), " per API call:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Creating an array consumes one data point."
        }), "\n", _jsx(_components.li, {
          children: "Updating arrays consumes one data point per value updated."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/user_guide/data_and_analytics/custom_data/custom_attributes/array_of_objects/",
          children: "arrays of objects"
        }), ", you may consume multiple data points with one API call:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Creating an array of objects consumes one data point ", _jsx(_components.em, {
            children: "per attribute added"
          }), " within each object. For example, creating two ", _jsx(_components.code, {
            children: "pet"
          }), " objects with four attributes each (", _jsx(_components.code, {
            children: "id"
          }), ", ", _jsx(_components.code, {
            children: "type"
          }), ", ", _jsx(_components.code, {
            children: "color"
          }), ", and ", _jsx(_components.code, {
            children: "size"
          }), ") consumes eight data points."]
        }), "\n", _jsxs(_components.li, {
          children: ["Updating an array of objects consumes one data point ", _jsx(_components.em, {
            children: "per attribute changed"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Removals consume one data point ", _jsx(_components.em, {
            children: "per removal criteria"
          }), ". For example, removing objects where ", _jsx(_components.code, {
            children: "id = 4"
          }), " removes one object and costs one data point. Removing objects where ", _jsx(_components.code, {
            children: "type = dog"
          }), " may remove many objects but still only consumes one data point."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing records that don't exist in Braze, Hightouch updates ", _jsx(_components.strong, {
          children: "all"
        }), " mapped attributes with the values from your query results. This means you consume one data point per attribute per created record."]
      }), "\n", _jsx(_components.p, {
        children: "When syncing new records in the model that already exist in Braze, Hightouch first gets the user attributes from Braze, calculates which columns actually changed, and then only sends the changes for those columns. Each updated attribute equals one data point consumed."
      }), "\n", _jsx(_components.p, {
        children: "When syncing records that have been previously synced to Braze, Hightouch compares the new record's columns to the previously sent record's columns, calculates which columns actually changed, and then only sends the changes for those columns. Each updated attribute equals one data point consumed."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Hightouch sync strategy aims for the lowest Braze data point consumption\npossible. Because Hightouch makes a ", _jsx(_components.code, {
            children: "GET"
          }), " request to first see what user\nattributes haven't changed since the last sync, sync speed is slower than\nother methods which overwrite all data in Braze and consume more data points."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "syncing-nested-or-array-attributes",
        children: "Syncing nested or array attributes"
      }), "\n", _jsxs(_components.p, {
        children: ["To sync nested or array attributes to Braze, structure your model data as JSON objects. In the sync configuration, map the JSON column to the corresponding Braze custom attribute. For an array of objects, the custom attribute's data type in Braze must be ", _jsx(_components.strong, {
          children: "array of objects"
        }), ", not ", _jsx(_components.strong, {
          children: "array"
        }), ". While array allows the addition and removal of values, it does not support updates to existing values. Array of objects supports those updates, along with adds and removes. If the attribute is typed as array and updates to existing values are attempted, sync runs will still show as successful because Braze accepts the request, but the nested updates are not applied. For arrays of objects, see the Braze documentation on ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/user_guide/data_and_analytics/custom_data/custom_attributes/nested_custom_attribute_support/",
          children: "nested custom attributes"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "multiple-requests-per-user",
        children: "Multiple requests per user"
      }), "\n", _jsxs(_components.p, {
        children: ["When updating an attribute's value in a nested object to ", _jsx(_components.code, {
          children: "null"
        }), ", Braze's API doesn't let you combine this change with other non", _jsx(_components.code, {
          children: "-null"
        }), " updates. Therefore, when updating values to ", _jsx(_components.code, {
          children: "null"
        }), ", Hightouch sends the full nested object with the ", _jsx(_components.code, {
          children: "_merge_objects"
        }), " parameter set to ", _jsx(_components.code, {
          children: "false"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Setting ", _jsx(_components.code, {
          children: "_merge_objects"
        }), " to ", _jsx(_components.code, {
          children: "true"
        }), " deep merges your update with the existing object data. See the Braze ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/user_guide/data_and_analytics/custom_data/custom_attributes/nested_custom_attribute_support/#api-request-body",
          children: "API documentation"
        }), " for more details."]
      }), "\n", _jsx(_components.p, {
        children: "This means the same user may require two update actions:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Updates to nested objects with non-null values with ", _jsx(_components.code, {
            children: "_merge_objects:true"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Updates setting one or more attributes in nested objects to ", _jsx(_components.code, {
            children: "null"
          }), " with ", _jsx(_components.code, {
            children: "_merge_objects:false"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After the sync runs, confirm the data landed in Braze:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["For user syncs, search for the test user in the Braze dashboard under ", _jsx(_components.strong, {
            children: "Audience > Search Users"
          }), " and confirm the mapped attributes appear with expected values."]
        }), "\n", _jsx(_components.li, {
          children: "For event syncs, check the user's profile activity feed to confirm events were received."
        }), "\n", _jsxs(_components.li, {
          children: ["For catalog syncs, go to ", _jsx(_components.strong, {
            children: "Data Settings > Catalogs"
          }), " and verify items were created or updated."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error message"
            }), _jsx(_components.th, {
              align: "left",
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Invalid phone number"
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Braze rejects a phone number as invalid. ", _jsx(_components.a, {
                href: "#invalid-phone-number",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "alias objects must be a hash"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["The User Alias Object isn't formatted as a JSON object. ", _jsx(_components.a, {
                href: "#alias-objects-must-be-a-hash",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Batch too large or payload exceeds Braze limits"
            }), _jsxs(_components.td, {
              align: "left",
              children: ["A payload exceeds Braze's size limits. ", _jsx(_components.a, {
                href: "#batch-too-large-or-payload-exceeds-braze-limits",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Invalid or missing user identifiers"
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Identifiers are missing or invalid, so rows are dropped. ", _jsx(_components.a, {
                href: "#invalid-or-missing-user-identifiers",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Event attribute type errors"
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Event or purchase properties don't match Braze's required types. ", _jsx(_components.a, {
                href: "#event-attribute-type-errors",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Catalog field type or schema mismatches"
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Catalog field types or names don't match the existing catalog schema. ", _jsx(_components.a, {
                href: "#catalog-field-type-or-schema-mismatches",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Subscription group ID or permission errors"
            }), _jsxs(_components.td, {
              align: "left",
              children: ["The subscription group ID is invalid or the API key lacks scopes. ", _jsx(_components.a, {
                href: "#subscription-group-id-or-permission-errors",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Campaign or Canvas trigger failures"
            }), _jsxs(_components.td, {
              align: "left",
              children: ["A trigger doesn't fire because identifiers or trigger properties are missing. ", _jsx(_components.a, {
                href: "#campaign-or-canvas-trigger-failures",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "rowsPerBatch must be less than or equal to 50"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["The advanced mapper's batch size exceeds 50 rows. ", _jsx(_components.a, {
                href: "#rows-per-batch-exceeds-the-limit",
                children: "More detail."
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "invalid-phone-number",
        children: "Invalid phone number"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "400 – The value provided for the phone number field is not a valid phone number\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Braze marks some phone numbers as invalid if they are deactivated, rejected by providers, on an ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/user_guide/message_building_by_channel/sms/keywords/optin_optout",
          children: "opt-out list"
        }), ", or already associated with another user. See Braze's guidance on ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/user_guide/message_building_by_channel/sms/phone_numbers/user_phone_numbers#importing-phone-numbers",
          children: "importing phone numbers"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Solution:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Validate phone number formatting in your source."
        }), "\n", _jsx(_components.li, {
          children: "Remove numbers that are known to be deactivated or opted out."
        }), "\n", _jsx(_components.li, {
          children: "Check for conflicting assignments of the same number to multiple users."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "alias-objects-must-be-a-hash",
        children: "Alias objects must be a hash"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "alias objects must be a hash\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The alias object is not in the expected format with both ", _jsx(_components.code, {
          children: "alias_label"
        }), " and ", _jsx(_components.code, {
          children: "alias_name"
        }), ". A common cause is mapping a string column (for example, a JSON string) to the User Alias Object field instead of a JSON object. The expected format looks like this:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"alias_label\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"label\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"alias_name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"name_1\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["See Braze's ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/api/objects_filters/user_alias_object",
          children: "User Alias Object"
        }), " documentation for the required structure."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Solution:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Ensure the model column mapped to the User Alias Object is of type ", _jsx(_components.strong, {
            children: "Object"
          }), ", not string."]
        }), "\n", _jsx(_components.li, {
          children: "Build the alias object in the correct shape before syncing."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "batch-too-large-or-payload-exceeds-braze-limits",
        children: "Batch too large or payload exceeds Braze limits"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "Max input length exceeded\nRequest body too large\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Braze returns errors when event payloads, user attributes, or catalog items exceed size limits."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Solution:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Reduce the number of mapped fields."
        }), "\n", _jsx(_components.li, {
          children: "Remove large nested objects or unnecessary attributes."
        }), "\n", _jsx(_components.li, {
          children: "Split very large catalogs or audiences across multiple syncs."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "invalid-or-missing-user-identifiers",
        children: "Invalid or missing user identifiers"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "Missing matching row\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Braze cannot update or target users unless identifiers are valid and non-null. You may also see dropped rows or partial updates."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Solution:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Prefer ", _jsx(_components.strong, {
            children: "external_id"
          }), " as the primary identifier when possible."]
        }), "\n", _jsx(_components.li, {
          children: "Ensure identifier columns are populated and stable in your model."
        }), "\n", _jsxs(_components.li, {
          children: ["For alias-based syncs, include both ", _jsx(_components.code, {
            children: "alias_name"
          }), " and ", _jsx(_components.code, {
            children: "alias_label"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "event-attribute-type-errors",
        children: "Event attribute type errors"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "TypeError\nInvalid event attribute\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Braze enforces strict types for event and purchase properties."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Solution:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Convert timestamps to ISO 8601 or Unix milliseconds."
        }), "\n", _jsx(_components.li, {
          children: "Ensure numeric fields (such as price or quantity) are not strings."
        }), "\n", _jsx(_components.li, {
          children: "Avoid nested objects or arrays in event attributes unless Braze explicitly supports them."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "catalog-field-type-or-schema-mismatches",
        children: "Catalog field type or schema mismatches"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "Field type mismatch\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Catalog syncs fail when field types change or when new attributes do not match the existing catalog schema. You may also see partial item failures."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Solution:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Keep catalog field names and types consistent across syncs."
        }), "\n", _jsx(_components.li, {
          children: "When schema changes are required, consider creating a new catalog (Braze catalog deletions are permanent)."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "subscription-group-id-or-permission-errors",
        children: "Subscription group ID or permission errors"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "Invalid subscription group ID\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Subscription syncs fail when the subscription group ID is invalid or its API key lacks required scopes. You may also see the subscription state not updating."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Solution:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Use the subscription group's ", _jsx(_components.strong, {
            children: "GUID"
          }), " (not the display name)."]
        }), "\n", _jsxs(_components.li, {
          children: ["Ensure the API key includes ", _jsx(_components.code, {
            children: "subscription.status.set"
          }), " or an equivalent permission."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "campaign-or-canvas-trigger-failures",
        children: "Campaign or Canvas trigger failures"
      }), "\n", _jsxs(_components.p, {
        children: ["A campaign or Canvas trigger fails silently or returns a ", _jsx(_components.code, {
          children: "400"
        }), " error."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Triggers do not fire when user identifiers or required trigger properties are missing."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Solution:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Confirm record matching uses a valid Braze identifier (for example, ", _jsx(_components.code, {
            children: "external_id"
          }), ")."]
        }), "\n", _jsx(_components.li, {
          children: "Verify that all required trigger properties exist in the model and are mapped in the sync configuration."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "rows-per-batch-exceeds-the-limit",
        children: "Rows per batch exceeds the limit"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "rowsPerBatch must be less than or equal to 50\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The advanced mapper's batch size exceeds the Braze API limit of 50 rows per batch."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Reduce the ", _jsx(_components.code, {
          children: "rowsPerBatch"
        }), " value in your sync's advanced mapper configuration to 50 or fewer."]
      }), "\n", _jsx(_components.h3, {
        id: "required-permissions-1",
        children: "Required permissions"
      }), "\n", _jsxs(_components.p, {
        children: ["If your syncs are failing, check that the REST API key you use to ", _jsx(_components.a, {
          href: "#api-key",
          children: "connect Braze to Hightouch"
        }), " has the ", _jsx(_components.a, {
          href: "#required-permissions",
          children: "correct permissions"
        }), " for your sync type or types."]
      }), "\n", _jsx(_components.h3, {
        id: "slow-initial-sync",
        children: "Slow initial sync"
      }), "\n", _jsxs(_components.p, {
        children: ["If you're using a Braze ID to ", _jsx(_components.a, {
          href: "#record-matching",
          children: "match user records"
        }), ", your initial sync may be slow. When Hightouch updates Braze with new records or fields, Hightouch queries users in Braze for ", _jsx(_components.a, {
          href: "/syncs/cdc",
          children: "change data capture"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Braze allows batch searches on external IDs, but only allows querying for one user at a time if using Braze IDs. That means Hightouch needs to make a separate request for each user if using Braze IDs."
      }), "\n", _jsx(_components.p, {
        children: "It's best to match records on external ID rather than Braze ID. This speeds up the initial sync and limits the number of requests to make to Braze."
      }), "\n", _jsx(_components.h3, {
        id: "audience-count-drops-to-0-when-adding-braze-as-a-destination",
        children: "Audience count drops to 0 when adding Braze as a destination"
      }), "\n", _jsxs(_components.p, {
        children: ["If your audience count drops to 0 after you add Braze as a destination, the audience's parent model is likely missing the identifier field that Braze requires (for example, ", _jsx(_components.code, {
          children: "external_id"
        }), " or ", _jsx(_components.code, {
          children: "braze_id"
        }), "). The destination filter needs matching identifiers to calculate the sendable count. Verify that your parent model includes the correct Braze identifier column and that values are populated for the users you expect to sync."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
